import { CheckIcon, ShoppingCartIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';

import useAccountQuery from 'api/queries/useAccountQuery';
import useCreditsQuery from 'api/queries/useCreditsQuery';
import useDiscountLadderQuery from 'api/queries/useDiscountLadderQuery';
import useSelectionsQuery from 'api/queries/useSelectionsQuery';
import useSubscriptionQuery from 'api/queries/useSubscriptionQuery';
import Button from 'honeydew/Button/Button';
import { useAccountPlusMembershipHook } from 'hooks/useAccountPlusMembershipHook/useAccountPlusMembershipHook';
import useGlobalStore from 'hooks/useGlobalStore';
import useOrderMin from 'hooks/useOrderMin';
import { getTotalProductQty } from 'utils/orderUtils';
import { getCartEBTSubtotal, getCartSubtotal, getCartTotal, PriceType } from 'utils/priceUtils';

/**
 * Toggles the cart sheet and shows cart state: line-item count, dollar amount (subtotal, SNAP
 * total, or "INCOMPLETE" below the order minimum), and a short "Added!" state after a product
 * is added. Renders nothing until selections are loaded.
 *
 * @param {Object} props
 * @param {boolean} [props.splitWithSavingsPill] - When true, stretches full width and wraps the
 *   control in a white, rounded container for the shop header row beside a savings pill.
 * @returns {ReactNode} The cart control, or `false` while selections are loading.
 */
function CartButton({ splitWithSavingsPill = false }) {
    const { productAdded, setCartOpen } = useGlobalStore();

    const { plusMembershipEligibleOrder, shippingFees } = useAccountPlusMembershipHook();
    const { plusMembershipShippingFee } = shippingFees ?? {};

    const { data: account } = useAccountQuery();
    const { data: credits = {} } = useCreditsQuery();
    const { data: discounts = {} } = useDiscountLadderQuery();
    const { data: selections } = useSelectionsQuery();
    const { data: subscription = {} } = useSubscriptionQuery();
    const { isOrderMinMet } = useOrderMin();

    if (!selections) return false;

    const priceType = plusMembershipEligibleOrder ? PriceType.PLUS_MEMBER : PriceType.DEFAULT;

    const cartQuantity = getTotalProductQty([...selections.selected, ...selections.selectedBundles]);

    // CUS-6365: For SNAP users with EBT payment, show final balance; otherwise show subtotal
    // Only calculate when order minimum is met (when we'll actually display it)
    let displayAmount = 0;

    if (isOrderMinMet) {
        // Check if SNAP/EBT payment is applied
        const ebtSubtotal = getCartEBTSubtotal({ selections });
        const hasEBTPayment = ebtSubtotal > 0;

        if (hasEBTPayment) {
            displayAmount = getCartTotal({
                account,
                credits,
                discounts,
                options: {
                    priceType,
                    ignoreOrderMin: !!subscription?.ebtCard,
                    isOrderMinMet,
                },
                plusMembership: { plusMembershipShippingFee },
                selections,
            });
        } else {
            displayAmount = getCartSubtotal({
                account,
                options: { priceType, isOrderMinMet },
                selections,
            });
        }
    }

    const button = (
        <Button
            onClick={() => setCartOpen((currVal) => !currVal)}
            size="md"
            rounded="full"
            variant={isOrderMinMet ? 'primary' : 'error'}
        >
            <span className="notranslate flex items-center text-body-mobile-md">
                {productAdded ? (
                    <>
                        <CheckIcon className="mr-5 mb-px w-20" />
                        Added!
                    </>
                ) : (
                    <>
                        <ShoppingCartIcon className="mr-10 w-20" />
                        <span className="mr-10">{cartQuantity}</span>
                        {isOrderMinMet ? (
                            <span className="relative top-[2px] font-grotesk">${displayAmount.toFixed(2)}</span>
                        ) : (
                            <p className="text-body-mobile-sm">INCOMPLETE</p>
                        )}
                    </>
                )}
            </span>
        </Button>
    );

    return (
        <span
            className={clsx({
                'md:w-[180px]': !splitWithSavingsPill,
                'w-full min-w-0': splitWithSavingsPill,
            })}
        >
            {splitWithSavingsPill ? (
                <span className="block w-full min-w-0 rounded-full bg-white">{button}</span>
            ) : (
                button
            )}
        </span>
    );
}

export default CartButton;
